/**
 * ThemeContext — Modern Card Theme (Feb 2026, Phase 1)
 *
 * Owns the customer-facing UI theme selection ("code" vs "modern") and
 * writes it to `<html data-theme="…">` so every Modern surface (CSS
 * variables under `:root[data-theme="modern"]`) re-skins instantly on
 * toggle without any component rerender.
 *
 * Resolution order (first hit wins):
 *   1. Authenticated user's `ui_theme` field from `/api/auth/me`
 *   2. localStorage `am_ui_theme` (mirrors #1 for instant paint on
 *      cold reload, before AuthContext hydrates)
 *   3. Default → 'code' (existing users see no visual change)
 *
 * Non-customer roles (dispatcher / driver / super_admin) are pinned to
 * 'code' regardless — the Modern theme scope is customer-only for
 * Phase 1. Dispatch ops surfaces stay on the terminal aesthetic.
 *
 * The provider persists changes via the shared `updateProfile` helper
 * on AuthContext, which PUTs `/api/users/profile { ui_theme }` and
 * merges the response into the user object.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'am_ui_theme';
const VALID_THEMES = new Set(['code', 'modern']);
const DEFAULT_THEME = 'code';

// Feb 2026 — Modern Theme UNFROZEN after Phase 3 shipped (Rewards Hub,
// Orders Modern shell, Profile Modern shell with toggle guard). Flip to
// `true` ONLY as an emergency rollback for inconsistent rendering.
const MODERN_THEME_FROZEN = false;

const ThemeContext = createContext(null);

// Read the persisted theme from localStorage. Isolated so SSR doesn't
// crash on the `window` reference.
function readStoredTheme() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID_THEMES.has(v) ? v : null;
  } catch {
    return null;
  }
}

function writeStoredTheme(theme) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // best-effort persist; ignore quota / privacy-mode errors
  }
}

// Applies the theme attribute to <html>. No-op if already set.
function applyThemeAttr(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (root.getAttribute('data-theme') !== theme) {
    root.setAttribute('data-theme', theme);
  }
}

export function ThemeProvider({ children }) {
  const { user, updateProfile } = useAuth();
  // Initial theme = localStorage mirror or default. Applied immediately
  // in the render phase via useState initializer so the very first paint
  // already carries the correct attribute (no theme-flash on reload).
  // Feb 2026 — while MODERN_THEME_FROZEN is true, ignore the mirror
  // entirely and seed with 'code' so even opted-in customers paint Code
  // on first frame. Prevents the inconsistent "half-Modern / half-Code"
  // flash on iOS PWA foreground/background cycles.
  const [theme, setThemeState] = useState(() =>
    MODERN_THEME_FROZEN ? DEFAULT_THEME : (readStoredTheme() || DEFAULT_THEME)
  );

  // Sync from the authenticated user record once it hydrates. Only
  // customer role gets to opt into Modern; other roles are pinned to
  // 'code' so dispatch/driver ops surfaces don't inherit the light theme.
  useEffect(() => {
    if (!user || !user.id) return;
    // Feb 2026 — while frozen, EVERY role (customer included) is
    // pinned to Code regardless of their stored `ui_theme`.
    if (MODERN_THEME_FROZEN) {
      if (theme !== 'code') setThemeState('code');
      return;
    }
    const role = user.role;
    if (role !== 'customer') {
      // Pin non-customer roles to 'code' but do NOT overwrite the
      // localStorage mirror — the customer's chosen theme should
      // reappear if they switch accounts back.
      if (theme !== 'code') setThemeState('code');
      return;
    }
    const userTheme = VALID_THEMES.has(user.ui_theme) ? user.ui_theme : DEFAULT_THEME;
    if (userTheme !== theme) {
      setThemeState(userTheme);
      writeStoredTheme(userTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.ui_theme, user?.role]);

  // Reflect the current theme onto <html data-theme="…"> whenever it
  // changes. Runs on mount too, guaranteeing every route below the
  // provider sees the resolved attribute.
  useEffect(() => {
    applyThemeAttr(theme);
  }, [theme]);

  // Public setter — updates local state + localStorage immediately for
  // instant paint, then persists to the backend in the background.
  // Persist failure DOESN'T rollback the local flip: the user still
  // sees their choice and the next successful profile write will sync.
  const setTheme = useCallback(async (next) => {
    if (!VALID_THEMES.has(next)) return;
    // Feb 2026 — while MODERN_THEME_FROZEN, swallow any attempt to flip
    // to Modern. `code` requests still pass through so the Phase 3
    // "reset me to Code" cleanup path on next login remains functional.
    if (MODERN_THEME_FROZEN && next !== 'code') return;
    setThemeState(next);
    writeStoredTheme(next);
    // Only persist for authenticated customers. Non-customer roles are
    // pinned to 'code' by the effect above so a hypothetical direct
    // call from a dispatcher would be a no-op on the server anyway.
    if (user && user.id && user.role === 'customer') {
      try {
        await updateProfile({ ui_theme: next });
      } catch {
        // Silent — the local flip persists, next successful update will sync.
      }
    }
  }, [user, updateProfile]);

  const value = useMemo(() => ({
    theme,
    isModern: theme === 'modern',
    isCode: theme === 'code',
    setTheme,
  }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Defensive default — component rendered outside provider will
    // safely fall back to the legacy theme.
    return { theme: DEFAULT_THEME, isModern: false, isCode: true, setTheme: () => {} };
  }
  return ctx;
}
